import React, { useState, useEffect } from 'react';
import { useAuth } from '../../../contexts/AuthContext';
import { pricingStrategyService } from '../../../services/financeService';
import { PricingStrategy } from '../../../types/finance';
import toast from 'react-hot-toast';
import { FaLightbulb, FaSave, FaSpinner, FaTrash, FaPlus } from 'react-icons/fa';
import { anthropicService } from '../../../services/anthropicService';

const StrategyTab: React.FC = () => {
  const { user } = useAuth();
  const [strategies, setStrategies] = useState<PricingStrategy[]>([]);
  const [selectedStrategy, setSelectedStrategy] = useState<PricingStrategy | null>(null);
  const [loading, setLoading] = useState(true);
  const [generating, setGenerating] = useState(false);
  
  // Form states
  const [businessType, setBusinessType] = useState('');
  const [productName, setProductName] = useState('');
  const [competitorInfo, setCompetitorInfo] = useState('');
  const [targetMarket, setTargetMarket] = useState('');
  const [businessGoals, setBusinessGoals] = useState('');
  const [recommendedStrategy, setRecommendedStrategy] = useState('');
  const [strategyDescription, setStrategyDescription] = useState('');
  const [implementationNotes, setImplementationNotes] = useState('');

  useEffect(() => {
    const fetchStrategies = async () => {
      try {
        setLoading(true);
        const data = await pricingStrategyService.getAll();
        setStrategies(data);
      } catch (error) {
        console.error('Error fetching strategies:', error);
        toast.error('Failed to load pricing strategies');
      } finally {
        setLoading(false);
      }
    };

    fetchStrategies();
  }, []);

  const handleSelectStrategy = async (id: string) => {
    try {
      const strategy = await pricingStrategyService.getById(id);
      setSelectedStrategy(strategy);
      setBusinessType(strategy.business_type);
      setProductName(strategy.product_name);
      setRecommendedStrategy(strategy.recommended_strategy);
      setStrategyDescription(strategy.strategy_description);
      setImplementationNotes(strategy.implementation_notes || '');
    } catch (error) {
      console.error('Error fetching strategy:', error);
      toast.error('Failed to load strategy details');
    }
  };

  const handleGenerateStrategy = async () => {
    if (!user) return;
    if (!businessType || !productName || !targetMarket || !businessGoals) {
      toast.error('Please fill all required fields');
      return;
    }

    try {
      setGenerating(true);
      
      const prompt = `
        As a pricing strategy expert, recommend the best pricing strategy for:
        
        Business Type: ${businessType}
        Product/Service: ${productName}
        Target Market: ${targetMarket}
        Business Goals: ${businessGoals}
        Competitor Info: ${competitorInfo || 'Not provided'}
        
        Provide:
        1. Recommended pricing strategy name
        2. Detailed description and why it's appropriate
        3. Practical implementation notes
        
        Format as JSON:
        {
          "strategy": "Strategy name",
          "description": "Detailed description",
          "implementation": "Implementation notes"
        }
      `;
      
      const response = await anthropicService.generateText(prompt);
      
      try {
        const jsonMatch = response.match(/\{[\s\S]*\}/); 
        if (jsonMatch) {
          const jsonResponse = JSON.parse(jsonMatch[0]);
          setRecommendedStrategy(jsonResponse.strategy);
          setStrategyDescription(jsonResponse.description);
          setImplementationNotes(jsonResponse.implementation);
          toast.success('Strategy generated successfully');
        } else {
          setRecommendedStrategy('Custom Strategy');
          setStrategyDescription(response);
          setImplementationNotes('');
          toast.error('Strategy generated but in unexpected format');
        }
      } catch (parseError) {
        console.error('Error parsing AI response:', parseError);
        setRecommendedStrategy('Custom Strategy');
        setStrategyDescription(response);
        setImplementationNotes('');
        toast.error('Strategy generated but in unexpected format');
      }
    } catch (error) {
      console.error('Error generating strategy:', error);
      toast.error('Failed to generate pricing strategy');
    } finally {
      setGenerating(false);
    }
  };

  const handleSaveStrategy = async () => {
    if (!user) return;
    if (!businessType || !productName || !recommendedStrategy || !strategyDescription) {
      toast.error('Please fill all required fields');
      return;
    }

    try {
      const strategyData: PricingStrategy = {
        user_id: user.id,
        business_type: businessType,
        product_name: productName,
        recommended_strategy: recommendedStrategy,
        strategy_description: strategyDescription,
        implementation_notes: implementationNotes
      };

      if (selectedStrategy) {
        const updated = await pricingStrategyService.update(selectedStrategy.id!, strategyData);
        setStrategies(strategies.map(s => s.id === updated.id ? updated : s));
        setSelectedStrategy(updated);
        toast.success('Strategy updated');
      } else {
        const created = await pricingStrategyService.create(strategyData);
        setStrategies([created, ...strategies]);
        setSelectedStrategy(created);
        toast.success('Strategy saved');
      }
    } catch (error) {
      console.error('Error saving strategy:', error);
      toast.error('Failed to save pricing strategy');
    }
  };

  const handleDeleteStrategy = async () => {
    if (!selectedStrategy) return;

    try {
      await pricingStrategyService.delete(selectedStrategy.id!);
      setStrategies(strategies.filter(s => s.id !== selectedStrategy.id));
      handleNewStrategy();
      toast.success('Strategy deleted');
    } catch (error) {
      console.error('Error deleting strategy:', error);
      toast.error('Failed to delete strategy');
    }
  };

  const handleNewStrategy = () => {
    setSelectedStrategy(null);
    setBusinessType('');
    setProductName('');
    setCompetitorInfo('');
    setTargetMarket('');
    setBusinessGoals('');
    setRecommendedStrategy('');
    setStrategyDescription('');
    setImplementationNotes('');
  };

  if (loading) {
    return <div className="p-8 text-center text-gray-500">Loading strategies...</div>;
  }

  return (
    <div className="grid gap-6 lg:grid-cols-3">
      {/* Sidebar - Strategy List */}
      <div className="lg:col-span-1">
        <div className="rounded-xl bg-gray-50 p-5 shadow-sm ring-1 ring-gray-200">
          <div className="mb-4 flex items-center justify-between">
            <h3 className="text-base font-semibold text-gray-900">Your Strategies</h3>
            <button
              onClick={handleNewStrategy}
              className="flex items-center gap-1 rounded-lg bg-primary px-3 py-1 text-xs font-semibold text-white transition hover:bg-primary/90"
            >
              <FaPlus className="text-xs" /> New
            </button>
          </div>
          
          {strategies.length === 0 ? (
            <p className="text-sm text-gray-500">No strategies saved yet</p>
          ) : (
            <ul className="space-y-2">
              {strategies.map(strategy => (
                <li 
                  key={strategy.id} 
                  className={`cursor-pointer rounded-lg p-3 transition-all ${
                    selectedStrategy?.id === strategy.id 
                      ? 'border-l-4 border-primary bg-blue-50 shadow-sm' 
                      : 'hover:bg-white'
                  }`}
                  onClick={() => handleSelectStrategy(strategy.id!)}
                >
                  <div className="text-sm font-medium text-gray-900">{strategy.recommended_strategy}</div>
                  <div className="text-xs text-gray-600">
                    {strategy.business_type} - {strategy.product_name}
                  </div>
                </li>
              ))}
            </ul>
          )}
        </div>
      </div>

      {/* Main Content */}
      <div className="lg:col-span-2">
        <div className="space-y-6">
          {/* Business Info */}
          <div className="rounded-xl bg-gray-50 p-5 shadow-sm ring-1 ring-gray-200">
            <h3 className="mb-4 text-base font-semibold text-gray-900">
              💼 Business Information
            </h3>
            <div className="grid gap-4 md:grid-cols-2">
              <div>
                <label className="mb-1 block text-sm font-medium text-gray-700">Business Type *</label>
                <input
                  type="text"
                  value={businessType}
                  onChange={(e) => setBusinessType(e.target.value)}
                  className="w-full rounded-lg border border-gray-300 px-3 py-2 text-sm focus:border-primary focus:outline-none focus:ring-2 focus:ring-primary/20"
                  placeholder="e.g., SaaS, Retail, Service"
                  required
                />
              </div>
              <div>
                <label className="mb-1 block text-sm font-medium text-gray-700">Product/Service *</label>
                <input
                  type="text"
                  value={productName}
                  onChange={(e) => setProductName(e.target.value)}
                  className="w-full rounded-lg border border-gray-300 px-3 py-2 text-sm focus:border-primary focus:outline-none focus:ring-2 focus:ring-primary/20"
                  placeholder="e.g., Project Management Tool"
                  required
                />
              </div>
            </div>
          </div>
          
          {/* AI Generation (only if no strategy yet) */}
          {!selectedStrategy || !recommendedStrategy ? (
            <div className="rounded-xl bg-gradient-to-br from-green-50 to-blue-50 p-5 shadow-sm ring-1 ring-green-200">
              <h3 className="mb-4 flex items-center gap-2 text-base font-semibold text-gray-900">
                <span className="flex h-8 w-8 items-center justify-center rounded-lg bg-yellow-500 text-white">
                  <FaLightbulb />
                </span>
                Generate Strategy with AI
              </h3>
              <div className="space-y-3">
                <div>
                  <label className="mb-1 block text-sm font-medium text-gray-700">Target Market *</label>
                  <input
                    type="text"
                    value={targetMarket}
                    onChange={(e) => setTargetMarket(e.target.value)}
                    className="w-full rounded-lg border border-gray-300 px-3 py-2 text-sm focus:border-primary focus:outline-none focus:ring-2 focus:ring-primary/20"
                    placeholder="e.g., Small businesses, Enterprise"
                  />
                </div>
                <div>
                  <label className="mb-1 block text-sm font-medium text-gray-700">Business Goals *</label>
                  <textarea
                    value={businessGoals}
                    onChange={(e) => setBusinessGoals(e.target.value)}
                    className="w-full rounded-lg border border-gray-300 px-3 py-2 text-sm focus:border-primary focus:outline-none focus:ring-2 focus:ring-primary/20"
                    rows={2}
                    placeholder="e.g., Market penetration, Maximize profit"
                  />
                </div>
                <div>
                  <label className="mb-1 block text-sm font-medium text-gray-700">Competitor Information</label>
                  <textarea
                    value={competitorInfo}
                    onChange={(e) => setCompetitorInfo(e.target.value)}
                    className="w-full rounded-lg border border-gray-300 px-3 py-2 text-sm focus:border-primary focus:outline-none focus:ring-2 focus:ring-primary/20"
                    rows={2}
                    placeholder="e.g., Main competitors and their pricing"
                  />
                </div>
                <button
                  onClick={handleGenerateStrategy}
                  disabled={generating}
                  className="flex items-center gap-2 rounded-lg bg-green-600 px-4 py-2 text-sm font-semibold text-white shadow-md transition hover:bg-green-700 disabled:opacity-50"
                >
                  {generating ? (
                    <>
                      <FaSpinner className="animate-spin" /> Generating...
                    </>
                  ) : (
                    <>
                      <FaLightbulb /> Generate Strategy
                    </>
                  )}
                </button>
              </div>
            </div>
          ) : null}
          
          {/* Strategy Details */}
          <div className="rounded-xl bg-gray-50 p-5 shadow-sm ring-1 ring-gray-200">
            <h3 className="mb-4 text-base font-semibold text-gray-900">
              📋 Strategy Details
            </h3>
            <div className="space-y-4">
              <div>
                <label className="mb-1 block text-sm font-medium text-gray-700">Recommended Strategy *</label>
                <input
                  type="text"
                  value={recommendedStrategy}
                  onChange={(e) => setRecommendedStrategy(e.target.value)}
                  className="w-full rounded-lg border border-gray-300 px-3 py-2 text-sm focus:border-primary focus:outline-none focus:ring-2 focus:ring-primary/20"
                  placeholder="e.g., Premium Pricing"
                  required
                />
              </div>
              <div>
                <label className="mb-1 block text-sm font-medium text-gray-700">Strategy Description *</label>
                <textarea
                  value={strategyDescription}
                  onChange={(e) => setStrategyDescription(e.target.value)}
                  className="w-full rounded-lg border border-gray-300 px-3 py-2 text-sm focus:border-primary focus:outline-none focus:ring-2 focus:ring-primary/20"
                  rows={6}
                  placeholder="Detailed description of the strategy"
                  required
                />
              </div>
              <div>
                <label className="mb-1 block text-sm font-medium text-gray-700">Implementation Notes</label>
                <textarea
                  value={implementationNotes}
                  onChange={(e) => setImplementationNotes(e.target.value)}
                  className="w-full rounded-lg border border-gray-300 px-3 py-2 text-sm focus:border-primary focus:outline-none focus:ring-2 focus:ring-primary/20"
                  rows={4}
                  placeholder="Practical steps to implement"
                />
              </div>
            </div>
          </div>
          
          {/* Actions */}
          <div className="flex justify-end gap-2">
            {selectedStrategy && (
              <button
                onClick={handleDeleteStrategy}
                className="flex items-center gap-2 rounded-lg border border-red-200 px-4 py-2 text-sm font-semibold text-red-600 transition hover:bg-red-50"
              >
                <FaTrash /> Delete
              </button>
            )}
            <button
              onClick={handleSaveStrategy}
              className="flex items-center gap-2 rounded-lg bg-primary px-4 py-2 text-sm font-semibold text-white transition hover:bg-primary/90"
            >
              <FaSave /> {selectedStrategy ? 'Update' : 'Save'}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};

export default StrategyTab;
